import React, { useEffect, useState, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import './Styles/globals.css';

// Eagerly load critical pages (Auth, QR Redirect, Public Preview)
import Auth from './Pages/Auth';
import AuthCallbackPage from './Pages/AuthCallbackPage';
import QRRedirect from './Pages/QRRedirect';
import PublicCampaignPreview from './Pages/PublicCampaignPreview';
import PublicInternalAdPreview from './Pages/PublicInternalAdPreview';
import MLInvitationLandingPage from './Pages/MLInvitationLandingPage';

// Components can remain eager (smaller, used across routes)
import AdminLayout from './components/AdminLayout';
import { AdminLoadingProvider } from './contexts/AdminLoadingContext';
import { CampaignWithNodeDetails } from './services/campaign/types';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserProfileProvider } from './contexts/UserProfileContext';
import { supabase } from './lib/supabase';
import { CampaignProvider, useCampaignContext } from './contexts/CampaignContext';
import { NodeProvider } from './contexts/NodeContext';
import { ToastProvider } from './contexts/ToastContext';
import ToastContainer from './components/ToastContainer';
import AdminMfaGate from './components/auth/AdminMfaGate';
import SecurityGuard from './components/SecurityGuard';
import PermissionGuard from './components/PermissionGuard';
import { createSecureLogger } from './utils/securityUtils';
import { buildAuthRedirectUrl, getAuthRedirectTarget } from './utils/authRedirect';
import NetworkStatusModal from './components/NetworkStatusModal';

// Lazy load all other pages for code splitting
const Dashboard = lazy(() => import('./Pages/Dashboard'));
const Popup = lazy(() => import('./Pages/Popup'));
const CampaignBuilder = lazy(() => import('./Pages/CampaignBuilder'));
const Campaigns = lazy(() => import('./Pages/Campaigns'));
const Analytics = lazy(() => import('./Pages/Analytics'));
const PaymentSuccess = lazy(() => import('./Pages/PaymentSuccess'));
const Notification = lazy(() => import('./Pages/Notification'));
const SmartTips = lazy(() => import('./Pages/SmartTips'));
const Settings = lazy(() => import('./Pages/Settings'));
const Profile = lazy(() => import('./Pages/Profile'));
const AdminDashboard = lazy(() => import('./Pages/AdminDashboard'));
const AdminNode = lazy(() => import('./Pages/AdminNode'));
const AdminAdsManager = lazy(() => import('./Pages/AdminAdsManager'));
const AdminLookalInternalAdsPage = lazy(() => import('./Pages/AdminLookalInternalAdsPage'));
const AdminCampaignApproval = lazy(() => import('./Pages/AdminCampaignApproval'));
const AdminQRCodeManager = lazy(() => import('./Pages/AdminQRCodeManager'));
const AdminReports = lazy(() => import('./Pages/AdminReports'));
const AdminLogs = lazy(() => import('./Pages/AdminLogs'));
const AdminTVDisplayLogs = lazy(() => import('./Pages/AdminTVDisplayLogs'));
const AdminSettings = lazy(() => import('./Pages/AdminSettings'));
const AdminConfigurationManagement = lazy(() => import('./Pages/AdminConfigurationManagement'));
const AdminNotifications = lazy(() => import('./Pages/AdminNotifications'));
const AdminSmartTips = lazy(() => import('./Pages/AdminSmartTips'));
const AdminCreditBonusTiers = lazy(() => import('./Pages/AdminCreditBonusTiers'));
const AdminSlotTiers = lazy(() => import('./Pages/AdminSlotTiers'));
const TVDisplaySetup = lazy(() => import('./Pages/TVDisplaySetup'));
const TVDisplayScreen = lazy(() => import('./Pages/TVDisplayScreen'));
const TVDisplayDiagnostics = lazy(() => import('./Pages/TVDisplayDiagnostics'));
const CampaignPreview = lazy(() => import('./Pages/CampaignPreview'));
const CreditTopUp = lazy(() => import('./Pages/CreditTopUp'));
const Transactions = lazy(() => import('./Pages/Transactions'));
const PaymentHistory = lazy(() => import('./Pages/PaymentHistory'));
const MasterLicenseeRegistrationPage = lazy(() => import('./Pages/Licensing/MasterLicenseeRegistrationPage'));
const PublisherRegistrationPage = lazy(() => import('./Pages/Licensing/PublisherRegistrationPage'));
const AdminLicensingApprovals = React.lazy(() => import('./Pages/AdminLicensingApprovals'));
const AdminLicensingMonitor = React.lazy(() => import('./Pages/AdminLicensingMonitor'));
const AdminAgreementTemplates = React.lazy(() => import('./Pages/AdminAgreementTemplates'));
const AdminPayoutReview = React.lazy(() => import('./Pages/AdminPayoutReview'));
const AdminSettlementBatches = React.lazy(() => import('./Pages/AdminSettlementBatches'));
const AdminRolesPermissions = React.lazy(() => import('./Pages/AdminRolesPermissions'));
const AdminOTAUpdatePage = React.lazy(() => import('./Pages/AdminOTAUpdatePage'));
const MasterLicenseeDashboard = React.lazy(() => import('./Pages/MasterLicenseeDashboard'));
const PublisherDashboard = React.lazy(() => import('./Pages/PublisherDashboard'));
const MLPairingPage = React.lazy(() => import('./Pages/Licensing/MLPairingPage'));
const PublisherPaymentReturn = React.lazy(() => import('./Pages/Licensing/PublisherPaymentReturn'));
const AdminMLInvitationsPage = React.lazy(() => import('./Pages/AdminMLInvitationsPage'));
const AdminCreateMLInvitationPage = React.lazy(() => import('./Pages/AdminCreateMLInvitationPage'));

// Loading fallback for Suspense
const PageLoader = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

interface NotificationItem {
  id: string;
  icon: string;
  title: string;
  desc: string;
  unread: boolean;
  type: 'Slot Expiry' | 'QR Expiry' | 'Suggestion' | 'System Update' | 'Campaign' | 'General';
  actions: ('check' | 'info' | 'eye' | 'delete')[];
  createdAt: Date;
  campaignId?: string;
}

const DashboardWrapper = React.memo(function DashboardWrapper({ campaigns, addNotification, unreadCount, setCampaigns, onLogout }: {
  campaigns: CampaignWithNodeDetails[];
  addNotification: (notification: Omit<NotificationItem, 'id' | 'unread' | 'createdAt'>) => void;
  unreadCount: number;
  setCampaigns: React.Dispatch<React.SetStateAction<CampaignWithNodeDetails[]>>;
  onLogout: () => void;
}) {
  const navigate = useNavigate();

  const handleUpdateCampaign = useCallback((updatedCampaign: CampaignWithNodeDetails) => {
    // Update the campaign in the campaigns state
    setCampaigns(prev => prev.map(campaign =>
      campaign.id === updatedCampaign.id ? updatedCampaign : campaign
    ));
  }, [setCampaigns]);

  const onCampaignsClick = useCallback(() => navigate('/campaigns'), [navigate]);
  const onAnalyticsClick = useCallback(() => navigate('/analytics'), [navigate]);
  const onNotificationClick = useCallback(() => navigate('/notifications'), [navigate]);
  const onSmartTipsClick = useCallback(() => navigate('/smarttips'), [navigate]);
  const onLaunchCampaign = useCallback(() => navigate('/snap2ads'), [navigate]);
  const onSnap2Ads = useCallback(() => navigate('/snap2ads'), [navigate]);
  const onSettingsClick = useCallback(() => navigate('/settings'), [navigate]);

  return (
    <Dashboard
      campaigns={campaigns}
      addNotification={addNotification}
      unreadCount={unreadCount}
      onCampaignsClick={onCampaignsClick}
      onAnalyticsClick={onAnalyticsClick}
      onNotificationClick={onNotificationClick}
      onSmartTipsClick={onSmartTipsClick}
      onLaunchCampaign={onLaunchCampaign}
      onSnap2Ads={onSnap2Ads}
      updateCampaign={handleUpdateCampaign}
      onSettingsClick={onSettingsClick}
      onLogout={onLogout}
    />
  );
});

const CampaignDetailsRedirect = React.memo(function CampaignDetailsRedirect() {
  const { campaignId } = useParams();
  const target = campaignId ? `/campaigns?highlight=${encodeURIComponent(campaignId)}` : '/campaigns';
  return <Navigate to={target} replace />;
});

const CampaignRenewRedirect = React.memo(function CampaignRenewRedirect() {
  const { campaignId } = useParams();
  const target = campaignId ? `/campaigns?highlight=${encodeURIComponent(campaignId)}` : '/campaigns';
  return <Navigate to={target} replace />;
});

const PaymentRedirect = React.memo(function PaymentRedirect() {
  const { paymentId } = useParams();
  const target = paymentId ? `/campaigns?payment=${encodeURIComponent(paymentId)}` : '/campaigns';
  return <Navigate to={target} replace />;
});

const PopupWrapper = React.memo(function PopupWrapper({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const handleClose = useCallback(() => {
    onClose();
    navigate('/dashboard');
  }, [onClose, navigate]);
  return <Popup onClose={handleClose} />;
});

const CampaignBuilderWrapper = React.memo(function CampaignBuilderWrapper({ onCampaignComplete, onLogout }: { onCampaignComplete: (campaign: CampaignWithNodeDetails) => void; onLogout: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Get edit campaign data from navigation state
  const editCampaign = location.state?.editCampaign as CampaignWithNodeDetails | undefined;

  // Get campaign form data and initial step when returning from payment page
  const campaignFormData = location.state?.campaignFormData;
  const initialStep = location.state?.initialStep;

  const handleCampaignUpdate = useCallback((updatedCampaign: CampaignWithNodeDetails) => {
    // For edit mode, we'll treat updates as completions
    onCampaignComplete(updatedCampaign);
  }, [onCampaignComplete]);

  const handlePayment = useCallback((campaignData: any) => {
    // Navigate to payment page with campaign data
    navigate('/payments', { state: { campaignData } });
  }, [navigate]);

  return (
    <CampaignBuilder
      onCampaignComplete={onCampaignComplete}
      onCampaignUpdate={handleCampaignUpdate}
      onPayment={handlePayment}
      editCampaign={editCampaign}
      initialStep={initialStep}
      campaignFormData={campaignFormData}
    />
  );
});

const CampaignsWrapper = React.memo(function CampaignsWrapper({ campaigns, addNotification, setCampaigns }: {
  campaigns: CampaignWithNodeDetails[];
  addNotification: (notification: Omit<NotificationItem, 'id' | 'unread' | 'createdAt'>) => void;
  setCampaigns: React.Dispatch<React.SetStateAction<CampaignWithNodeDetails[]>>;
}) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { refreshData } = useCampaignContext();

  // Refresh campaigns only once when this component mounts
  useEffect(() => {
    refreshData(); // Use context's refresh function
  }, [refreshData]);

  const handleLogout = useCallback(async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [signOut, navigate]);

  const handleUpdateCampaign = useCallback((updatedCampaign: CampaignWithNodeDetails) => {
    setCampaigns(prev => prev.map(campaign =>
      campaign.id === updatedCampaign.id ? updatedCampaign : campaign
    ));
  }, [setCampaigns]);

  return (
    <Campaigns
      campaigns={campaigns}
      addNotification={addNotification}
      updateCampaign={handleUpdateCampaign}
      setCampaigns={setCampaigns}
      refreshCampaigns={refreshData}
    />
  );
});

const AnalyticsWrapper = React.memo(function AnalyticsWrapper({ addNotification }: { addNotification: (notification: Omit<NotificationItem, 'id' | 'unread' | 'createdAt'>) => void }) {
  const { campaigns } = useCampaignContext();
  return (
    <Analytics
      campaigns={campaigns}
      addNotification={addNotification}
    />
  );
});

const PaymentSuccessWrapper = React.memo(function PaymentSuccessWrapper() {
  return <PaymentSuccess />;
});

const NotificationWrapper = React.memo(function NotificationWrapper() {
  return <Notification />;
});

const SmartTipsWrapper = React.memo(function SmartTipsWrapper() {
  return (
    <UserProfileProvider>
      <SmartTips />
    </UserProfileProvider>
  );
});

const ProfileWrapper = React.memo(function ProfileWrapper() {
  return <Profile />;
});

const SettingsWrapper = React.memo(function SettingsWrapper() {
  const { user: authUser } = useAuth();

  // Create a mock user object for the Settings component - memoized to prevent re-creation
  const mockUser = useMemo(() => ({
    fullName: authUser?.user_metadata?.full_name || authUser?.email?.split('@')[0] || '',
    email: authUser?.email || '',
    company: authUser?.user_metadata?.business_name || '',
    darkMode: false,
    language: 'English'
  }), [authUser?.user_metadata?.full_name, authUser?.email, authUser?.user_metadata?.business_name]);

  const handleUpdateUser = useCallback((data: any) => {
    // Handle user updates here
  }, []);

  return (
    <Settings
      user={mockUser}
      updateUser={handleUpdateUser}
    />
  );
});

// Utility to convert Campaign to AdCampaign
interface AdCampaign {
  id: string;
  title: string;
  founder: string;
  founderEmail: string;
  status: string;
  snapgoal: string[];
  industry: string[];
  mediaType: string[];
  node: string;
  qrType: string;
  cta: string;
  schedule: string;
  goal: string;
  dateRange: string;
  previewImg: string;
  adStatus: string;
}

function campaignToAdCampaign(c: CampaignWithNodeDetails): AdCampaign {
  return {
    id: c.id,
    title: c.name,
    founder: '',
    founderEmail: '',
    status: c.status,
    snapgoal: [],
    industry: [],
    mediaType: [],
    node: '',
    qrType: c.qr_type,
    cta: c.cta,
    schedule: '',
    goal: '',
    dateRange: '',
    previewImg: '',
    adStatus: 'Active',
  };
}

// TV Display redirect that preserves query parameters (deviceId, creds)
const TVDisplayRedirect = React.memo(function TVDisplayRedirect() {
  const location = useLocation();
  // Preserve query string when redirecting to setup
  const to = `/tv-display/setup${location.search}`;
  return <Navigate to={to} replace />;
});

// Main App Router Component that uses auth context
function AppRouter() {
  const { user, loading, signOut, isAdmin = false, isAdminLoading = false, adminPermissions = [] } = useAuth();
  const location = useLocation();
  
  // Check for logout by being on auth page - this is the key fix
  const isOnAuthPage = location.pathname === '/auth';
  
  // Check for logout parameter to prevent auto-login after logout
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  
  // Update isLoggedOut when location changes
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const loggedOut = urlParams.get('logout') === 'true';
    setIsLoggedOut(loggedOut);
    
  }, [location.pathname, location.search]);

  const [campaigns, setCampaigns] = useState<CampaignWithNodeDetails[]>([]);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [popupSeen, setPopupSeen] = useState(false);
  const [popupStatusLoading, setPopupStatusLoading] = useState(true);
  const [shouldRedirectToPopup, setShouldRedirectToPopup] = useState(false);
  const [redirectDecisionMade, setRedirectDecisionMade] = useState(false);

  // Track if popup has been shown in current session to prevent multiple shows
  const [popupShownThisSession, setPopupShownThisSession] = useState(() => {
    return sessionStorage.getItem('popupShownThisSession') === 'true';
  });

  // Fetch authoritative popup status from database - ONLY source of truth
  useEffect(() => {
    // Skip if on auth page
    if (isOnAuthPage) {
      setShouldRedirectToPopup(false);
      setRedirectDecisionMade(false);
      setPopupStatusLoading(false);
      return;
    }

    // Reset popup state when no user
    if (!user) {
      setPopupSeen(false);
      setPopupShownThisSession(false);
      setShouldRedirectToPopup(false);
      setRedirectDecisionMade(false);
      setPopupStatusLoading(false);
      sessionStorage.removeItem('popupShownThisSession');
      return;
    }

    // Admin users never see popup
    if (isAdminLoading) {
      setPopupStatusLoading(true);
      return;
    }

    if (isAdmin) {
      setPopupSeen(true);
      setPopupShownThisSession(true);
      setShouldRedirectToPopup(false);
      setRedirectDecisionMade(true);
      setPopupStatusLoading(false);
      return;
    }

    const fetchStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('user_popup_status')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.warn('Could not fetch user_popup_status:', error);
          // If database error, show popup to be safe
          setPopupSeen(false);
          setShouldRedirectToPopup(true);
          setRedirectDecisionMade(true);
          setPopupStatusLoading(false);
          return;
        }

        if (data) {
          const now = new Date();
          const nextShowAt = data.next_show_at ? new Date(data.next_show_at) : null;
          const isSelected = data.last_event === 'selected';
          // Skip popup if user already selected OR next_show_at is in future
          const shouldSkip = Boolean(isSelected || (nextShowAt && now < nextShowAt));

          setPopupSeen(shouldSkip);

          // Set redirect decision here - single source of truth
          setShouldRedirectToPopup(!shouldSkip);
          setRedirectDecisionMade(true);

          if (nextShowAt && now < nextShowAt) {
            sessionStorage.setItem('popupShownThisSession', 'true');
            setPopupShownThisSession(true);
          }
        } else {
          // No status row yet - user hasn't interacted with popup
          setPopupSeen(false);
          // Set redirect decision here too
          setShouldRedirectToPopup(true);
          setRedirectDecisionMade(true);
        }
      } catch (err) {
        console.error('Error fetching popup status:', err);
        setPopupSeen(false);
        // On error, show popup to be safe
        setShouldRedirectToPopup(true);
        setRedirectDecisionMade(true);
      } finally {
        setPopupStatusLoading(false);
      }
    };

    fetchStatus();
  }, [user, user?.id, user?.email, isOnAuthPage, isAdmin, isAdminLoading]);

  // Reset redirect state when user changes
  useEffect(() => {
    setShouldRedirectToPopup(false);
    setRedirectDecisionMade(false);
  }, [user?.id]);

  // Defensive guard: if we have a user but app-level profile/context may not be ready,
  // show a short loading UI to avoid rendering an empty/white screen. After a short
  // timeout we proceed to routes to avoid blocking indefinitely.
  const [holdRender, setHoldRender] = useState(false);
  useEffect(() => {
    if (user) {
      setHoldRender(true);
      const t = setTimeout(() => setHoldRender(false), 1200); // 1.2s grace period
      return () => clearTimeout(t);
    } else {
      setHoldRender(false);
    }
  }, [user]);

  // Centralized logout handler - defined early to avoid conditional hook issues
  const handlePopupClose = useCallback(() => {
    setPopupSeen(true);
    setPopupShownThisSession(true);
    sessionStorage.setItem('popupShownThisSession', 'true');
    // Note: Database is updated by Popup component via record_popup_event RPC
  }, []);

  const addCampaign = useCallback(async (campaign: CampaignWithNodeDetails) => {
    // Campaign is added to context via CampaignContext's addCampaign
    // The context handles refreshing data from the database
  }, []);

  const addNotification = useCallback((notification: Omit<NotificationItem, 'id' | 'unread' | 'createdAt'>) => {
    const newNotification: NotificationItem = {
      ...notification,
      id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      unread: true,
      createdAt: new Date()
    };
    setNotifications(prev => [newNotification, ...prev]);
  }, []);

  const markNotificationAsRead = useCallback((notificationId: string) => {
    setNotifications(prev => prev.map(n =>
      n.id === notificationId ? { ...n, unread: false } : n
    ));
  }, []);

  const markAllNotificationsAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
  }, []);

  const deleteNotification = useCallback((notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  // Use useMemo for computed value instead of function
  const unreadCount = useMemo(() => {
    return notifications.filter(n => n.unread).length;
  }, [notifications]);

  // Centralized logout handler - defined early to avoid conditional hook issues
  const handleLogout = useCallback(async () => {
    try {
      await signOut();
      // Clear session storage for popup state
      sessionStorage.removeItem('popupShownThisSession');
      // Navigate to auth with logout parameter
      window.location.href = '/auth?logout=true';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [signOut]);

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // CRITICAL: If we're on auth page AND (no user OR explicitly logged out), return auth routes
  if (isOnAuthPage && (!user || isLoggedOut)) {
    return (
      <Routes>
        <Route path="/auth" element={<Auth />} />
        <Route path="*" element={<Navigate to="/auth" />} />
      </Routes>
    );
  }

  if (user && isAdminLoading) {
    return <PageLoader />;
  }

  // If we're on auth page but user IS logged in (and not explicitly logged out), redirect them to appropriate dashboard
  if (isOnAuthPage && user && !isLoggedOut) {
    const userIsAdmin = isAdmin;
    const authRedirectTarget = getAuthRedirectTarget(
      location.search,
      userIsAdmin ? '/admin/dashboard' : '/dashboard'
    );

    return (
      <Routes>
        <Route path="*" element={<Navigate to={authRedirectTarget} replace />} />
      </Routes>
    );
  }

  // Create admin logout handler (uses centralized handler)
  const handleAdminLogout = handleLogout;

  return (
    <Routes>
      {/* QR Redirect Route - Public, accessible without authentication */}
      <Route path="/qr/:qrId" element={<QRRedirect />} />

      {/* Campaign Preview Route - Public, for Playwright screenshot generation */}
      <Route path="/campaign-preview/:campaignId" element={<PublicCampaignPreview />} />

      {/* Internal ad Zone A preview route - Public, for Playwright screenshot generation */}
      <Route path="/internal-ad-preview" element={<PublicInternalAdPreview />} />

      {/* ML Invitation Landing Page - Public, no auth required */}
      <Route path="/invite/ml/:token" element={<MLInvitationLandingPage />} />

      {/* Auth callback route - public, used for OAuth and magic link redirects */}
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* Login Route - For when user needs to login with a specific redirect */}
      <Route path="/login" element={<Auth />} />

      {/* TV Display Routes - Public, accessible with access key authentication */}
      <Route path="/tv-display" element={<TVDisplayRedirect />} />
      <Route path="/tv-display/setup" element={<TVDisplaySetup />} />
      <Route path="/tv-display/:nodeId" element={<TVDisplayScreen />} />
      <Route path="/tv-display/:nodeId/diagnostics" element={<TVDisplayDiagnostics />} />

      {!user ? (
        // Not authenticated routes
        <>
          <Route path="/auth" element={<Auth />} />
          <Route path="*" element={<Navigate to={buildAuthRedirectUrl(location.pathname, location.search)} replace />} />
        </>
      ) : isAdmin || adminPermissions.length > 0 ? (
          // Admin routes with shared loading provider and security guards
          <>
            <Route path="/admin/*" element={
              <SecurityGuard allowedRoles={['admin']}>
                <AdminMfaGate>
                  <AdminLoadingProvider>
                    <AdminLayout onLogout={handleAdminLogout}>
                      <Routes>
                        <Route path="dashboard" element={<AdminDashboard />} />
                        <Route path="notifications" element={<PermissionGuard permission="notifications"><AdminNotifications /></PermissionGuard>} />
                        <Route path="smart-tips" element={<PermissionGuard permission="smart-tips"><AdminSmartTips /></PermissionGuard>} />
                        <Route path="credit-bonus-tiers" element={<PermissionGuard permission="credit-bonus"><AdminCreditBonusTiers /></PermissionGuard>} />
                        <Route path="slot-tiers" element={<PermissionGuard permission="slot-tiers"><AdminSlotTiers /></PermissionGuard>} />
                        <Route path="ads" element={<PermissionGuard permission="ads"><AdminAdsManager /></PermissionGuard>} />
                        <Route path="ads-manager" element={<PermissionGuard permission="ads"><AdminAdsManager /></PermissionGuard>} />
                        <Route path="lookal-internal-ads" element={<PermissionGuard permission="lookal-internal-ads"><AdminLookalInternalAdsPage /></PermissionGuard>} />
                        <Route path="campaign-approval" element={<PermissionGuard permission="campaign-approval"><AdminCampaignApproval /></PermissionGuard>} />
                        <Route path="qrcodes" element={<PermissionGuard permission="qrcodes"><AdminQRCodeManager /></PermissionGuard>} />
                        <Route path="nodes" element={<PermissionGuard permission="nodes"><AdminNode /></PermissionGuard>} />
                        <Route path="reports" element={<PermissionGuard permission="reports"><AdminReports /></PermissionGuard>} />
                        <Route path="logs" element={<PermissionGuard permission="logs"><AdminLogs /></PermissionGuard>} />
                        <Route path="tv-display-logs" element={<PermissionGuard permission="tv-display-logs"><AdminTVDisplayLogs /></PermissionGuard>} />
                        <Route path="settings" element={<PermissionGuard permission="settings"><AdminSettings /></PermissionGuard>} />
                        <Route path="configuration" element={<PermissionGuard permission="configuration"><AdminConfigurationManagement /></PermissionGuard>} />
                        <Route path="licensing-approvals" element={<PermissionGuard permission="licensing-approvals"><AdminLicensingApprovals /></PermissionGuard>} />
                        <Route path="licensing-monitor" element={<PermissionGuard permission="licensing-monitor"><AdminLicensingMonitor /></PermissionGuard>} />
                        <Route path="agreement-templates" element={<PermissionGuard permission="agreement-templates"><AdminAgreementTemplates /></PermissionGuard>} />
                        <Route path="payout-review" element={<PermissionGuard permission="payout-review"><AdminPayoutReview /></PermissionGuard>} />
                        <Route path="settlement-batches" element={<PermissionGuard permission="settlement-batches"><AdminSettlementBatches /></PermissionGuard>} />
                        <Route path="ota-updates" element={<PermissionGuard permission="ota-updates"><AdminOTAUpdatePage /></PermissionGuard>} />
                        <Route path="ml-invitations/new" element={<PermissionGuard permission="ml-invitations"><AdminCreateMLInvitationPage /></PermissionGuard>} />
                        <Route path="ml-invitations" element={<PermissionGuard permission="ml-invitations"><AdminMLInvitationsPage /></PermissionGuard>} />
                        <Route path="roles-permissions" element={<PermissionGuard permission="roles-permissions"><AdminRolesPermissions /></PermissionGuard>} />
                        <Route path="*" element={<AdminDashboard />} />
                      </Routes>
                    </AdminLayout>
                  </AdminLoadingProvider>
                </AdminMfaGate>
              </SecurityGuard>
            } />
            <Route path="/auth" element={<Navigate to="/admin/dashboard" />} />
            <Route path="*" element={<Navigate to="/admin/dashboard" />} />
          </>
        ) : (
          // Regular user routes with security guards
          <>
            <Route path="/" element={<Navigate to={popupStatusLoading || !redirectDecisionMade || !shouldRedirectToPopup ? "/dashboard" : "/popup"} />} />
            <Route path="/auth" element={<Navigate to={popupStatusLoading || !redirectDecisionMade || !shouldRedirectToPopup ? "/dashboard" : "/popup"} />} />
            <Route path="/popup" element={
              <SecurityGuard>
                <PopupWrapper onClose={handlePopupClose} />
              </SecurityGuard>
            } />
            <Route path="/dashboard" element={
              <SecurityGuard>
                <DashboardWrapper campaigns={campaigns} addNotification={addNotification} unreadCount={unreadCount} setCampaigns={setCampaigns} onLogout={handleLogout} />
              </SecurityGuard>
            } />
            <Route path="/snap2ads" element={
              <SecurityGuard>
                <CampaignBuilderWrapper onCampaignComplete={addCampaign} onLogout={handleLogout} />
              </SecurityGuard>
            } />
            <Route path="/campaigns" element={
              <SecurityGuard>
                <CampaignsWrapper campaigns={campaigns} addNotification={addNotification} setCampaigns={setCampaigns} />
              </SecurityGuard>
            } />
            {/* Legacy notification action_url support (avoid blank pages) */}
            <Route path="/notification" element={<Navigate to="/notifications" replace />} />
            <Route path="/campaigns/:campaignId" element={<CampaignDetailsRedirect />} />
            <Route path="/campaigns/:campaignId/renew" element={<CampaignRenewRedirect />} />
            <Route path="/payment/:paymentId" element={<PaymentRedirect />} />
            <Route path="/analytics" element={
              <SecurityGuard>
                <AnalyticsWrapper addNotification={addNotification} />
              </SecurityGuard>
            } />
            <Route path="/payment-success" element={
              <SecurityGuard>
                <PaymentSuccessWrapper />
              </SecurityGuard>
            } />
            <Route path="/top-up" element={
              <SecurityGuard>
                <CreditTopUp />
              </SecurityGuard>
            } />
            <Route path="/transactions" element={
              <SecurityGuard>
                <Transactions />
              </SecurityGuard>
            } />
            <Route path="/payment-history" element={
              <SecurityGuard>
                <PaymentHistory />
              </SecurityGuard>
            } />
            <Route path="/notification" element={<Navigate to="/notifications" replace />} />
            <Route path="/notifications" element={
              <SecurityGuard>
                <NotificationWrapper />
              </SecurityGuard>
            } />
            <Route path="/smarttips" element={
              <SecurityGuard>
                <SmartTipsWrapper />
              </SecurityGuard>
            } />
            <Route path="/profile" element={
              <SecurityGuard>
                <ProfileWrapper />
              </SecurityGuard>
            } />
            <Route path="/settings" element={<Navigate to="/profile" replace />} />
            <Route path="/dashboard/ml/pair" element={
              <SecurityGuard>
                <Suspense fallback={<PageLoader />}>
                  <MLPairingPage />
                </Suspense>
              </SecurityGuard>
            } />
            <Route path="/dashboard/ml" element={
              <SecurityGuard>
                <Suspense fallback={<PageLoader />}>
                  <MasterLicenseeDashboard />
                </Suspense>
              </SecurityGuard>
            } />
            <Route path="/dashboard/publisher" element={
              <SecurityGuard>
                <Suspense fallback={<PageLoader />}>
                  <PublisherDashboard />
                </Suspense>
              </SecurityGuard>
            } />
            <Route path="/license/register/ml" element={
              <SecurityGuard>
                <MasterLicenseeRegistrationPage />
              </SecurityGuard>
            } />
            <Route path="/license/register/publisher" element={
              <SecurityGuard>
                <PublisherRegistrationPage />
              </SecurityGuard>
            } />
            <Route path="/license/register/publisher/payment-return" element={
              <SecurityGuard>
                <PublisherPaymentReturn />
              </SecurityGuard>
            } />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </>
      )}
    </Routes>
  );
}

const NetworkStatusModalWrapper = React.memo(function NetworkStatusModalWrapper() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const isOnlineRef = useRef(isOnline);
  const location = useLocation();

  // Keep ref in sync with state to avoid stale closure in fetch wrapper
  useEffect(() => {
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    // Listen for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Also detect network errors from fetch failures
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        // If fetch succeeds but we thought we were offline, update status
        // Use ref to get current value without stale closure
        if (!isOnlineRef.current && navigator.onLine) {
          setIsOnline(true);
        }
        return response;
      } catch (error) {
        // Check if it's a network error
        if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
          setIsOnline(false);
        }
        throw error;
      }
    };

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.fetch = originalFetch;
    };
  }, []);

  // Exclude modal from TV display routes (they should work offline)
  const isTVDisplayRoute = location.pathname.startsWith('/tv-display/');

  if (isTVDisplayRoute) {
    return null;
  }

  return <NetworkStatusModal isOnline={isOnline} />;
});

function App() {
  useEffect(() => {
    const darkMode = localStorage.getItem('darkMode') === 'true';
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  return (
    <Router>
      <ToastProvider>
        <AuthProvider>
          <CampaignProvider>
            <NodeProvider>
              <NetworkStatusModalWrapper />
              <ToastContainer />
              <AppRouter />
            </NodeProvider>
          </CampaignProvider>
        </AuthProvider>
      </ToastProvider>
    </Router>
  );
}

export default App;
